import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Dropdown } from "../form-components/Dropdown";
import ScopeNodeTreeSelect from "./ScopeNodeTreeSelect";

const OPERATORS = [
  { value: "equals", label: "is" },
  { value: "in_subtree", label: "is in subtree of" },
];

/**
 * ScopeClauseCard — vertical card editor for a single scope clause.
 *
 * Collapsed: summary line + edit/delete icons
 * Expanded: full-width dimension select, segmented operator control, value picker
 *
 * Identification uses a string clauseId (UUID) instead of a numeric index.
 */
const ScopeClauseCard = ({
  clause,
  clauseId,
  dimensions = [],
  isExpanded,
  onToggle,
  onChange,
  onRemove,
}) => {
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Measure content height for smooth animation
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isExpanded, clause.dimension_id, clause.operator, clause.node_id]);

  const dim = dimensions.find((d) => d.id === clause.dimension_id);
  const op = OPERATORS.find((o) => o.value === clause.operator);
  const isComplete =
    clause.dimension_id != null &&
    clause.dimension_id > 0 &&
    clause.node_id != null &&
    clause.node_id > 0;

  // Summary text for collapsed state
  const summaryText = isComplete
    ? `${dim?.name || clause.dimension_id} \u00b7 ${op?.label || clause.operator} \u00b7 ${clause.node_label || clause.node_id}`
    : "Incomplete condition";

  const handleDimensionChange = (opt) => {
    onChange(clauseId, {
      ...clause,
      dimension_id: opt?.value ?? null,
      // Reset node when dimension changes
      node_id: null,
      node_label: null,
    });
  };

  const handleOperatorChange = (value) => {
    onChange(clauseId, { ...clause, operator: value });
  };

  const handleNodeChange = (opt) => {
    onChange(clauseId, {
      ...clause,
      node_id: opt?.value ?? null,
      node_label: opt?.label ?? null,
    });
  };

  return (
    <div className="border border-card-bd rounded-card bg-card overflow-hidden">
      {/* Collapsed header / toggle bar */}
      <div className="flex items-center justify-between px-4 py-3 hover:bg-surface-2 transition-colors duration-150">
        <button
          type="button"
          className="flex items-center gap-2 min-w-0 cursor-pointer select-none bg-transparent border-0 p-0 text-left flex-1"
          onClick={() => onToggle(clauseId)}
        >
          <FontAwesomeIcon
            icon={icon({ name: "chevron-right", style: "solid" })}
            className={`text-text-2 text-xs transition-transform duration-200 ${
              isExpanded ? "rotate-90" : ""
            }`}
          />
          <span
            className={`text-sm truncate ${
              isComplete ? "text-text" : "text-text-2 italic"
            }`}
          >
            {summaryText}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onRemove(clauseId)}
          className="ml-2 p-1 text-text-2 hover:text-danger transition-colors flex-shrink-0 cursor-pointer"
          title="Remove condition"
          aria-label="Remove condition"
        >
          <FontAwesomeIcon
            icon={icon({ name: "trash", style: "solid" })}
            className="text-xs"
          />
        </button>
      </div>

      {/* Expanded editor */}
      <div
        style={{
          maxHeight: isExpanded ? `${contentHeight + 32}px` : "0px",
          opacity: isExpanded ? 1 : 0,
        }}
        className="transition-all duration-200 ease-in-out overflow-hidden"
      >
        <div ref={contentRef} className="px-4 pb-4 pt-1 space-y-4">
          {/* Dimension */}
          <div>
            <span className="block text-xs font-medium text-text-2 mb-1">
              Dimension
            </span>
            <Dropdown
              options={dimensions.map((d) => ({
                value: d.id,
                label: d.name,
              }))}
              selectedValue={
                dim ? { value: dim.id, label: dim.name } : null
              }
              setSelectedValue={(opt) => opt && handleDimensionChange(opt)}
              fullWidth
              menuPortalTarget={document.body}
            />
            {isExpanded && !clause.dimension_id && (
              <p className="text-xs text-text-2 mt-1">
                Select a dimension.
              </p>
            )}
          </div>

          {/* Operator — segmented control */}
          <div>
            <span className="block text-xs font-medium text-text-2 mb-1">
              Operator
            </span>
            <div className="flex rounded-ctrl border border-border overflow-hidden">
              {OPERATORS.map((opItem) => (
                <button
                  key={opItem.value}
                  type="button"
                  className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors duration-100 ${
                    clause.operator === opItem.value
                      ? "bg-primary-soft text-primary border-primary"
                      : "bg-card text-text-2 hover:bg-surface-2"
                  } ${
                    clause.dimension_id
                      ? "cursor-pointer"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                  onClick={() => {
                    if (clause.dimension_id) {
                      handleOperatorChange(opItem.value);
                    }
                  }}
                  disabled={!clause.dimension_id}
                >
                  {opItem.label}
                </button>
              ))}
            </div>
          </div>

          {/* Value picker */}
          <div>
            <span className="block text-xs font-medium text-text-2 mb-1">
              Value
            </span>
            <ScopeNodeTreeSelect
              dimensionId={clause.dimension_id}
              value={clause.node_id}
              label={clause.node_label}
              onChange={handleNodeChange}
              placeholder="Select a dimension first"
            />
            {isExpanded &&
              clause.dimension_id &&
              clause.node_id == null && (
                <p className="text-xs text-text-2 mt-1">
                  Select a value to complete this condition.
                </p>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

ScopeClauseCard.propTypes = {
  clause: PropTypes.shape({
    dimension_id: PropTypes.number,
    operator: PropTypes.string,
    node_id: PropTypes.number,
    node_label: PropTypes.string,
  }).isRequired,
  clauseId: PropTypes.string.isRequired,
  dimensions: PropTypes.array,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ScopeClauseCard;
